import React, { useState } from 'react';
import { Box, Drawer, Card, CardContent, CardHeader, Typography, CircularProgress } from '@mui/material';
import api from "../../../../services/api";
import FormAddEditToolbar from "../../../../components/global/AddEditBar";
import { useNavigate } from 'react-router-dom';
import { useNotifier } from "muinotify";
import RecordingsForm from "../RecordingsForm";
import useSWR, { mutate } from 'swr';
import { useParams } from "react-router";
import ErrorNotification from '../../../../components/ErrorNotification/ErrorNotification';
import * as yup from 'yup';

const validationSchema = yup.object().shape({
  p_id: yup
    .number()
    .typeError('Η παραγωγή πρέπει να είναι αριθμός')
    .required('Η παραγωγή απαιτείται')
    .positive('Η παραγωγή πρέπει να είναι θετικός αριθμός')
    .integer('Η παραγωγή πρέπει να είναι ακέραιος αριθμός')
    .transform((value, originalValue) => {
      // Convert empty string, null, or undefined to undefined (which will fail required)
      if (originalValue === "" || originalValue === null || originalValue === undefined) {
        return undefined;
      }
      // Convert to number
      const numValue = Number(originalValue);
      return isNaN(numValue) ? undefined : numValue;
    }),

  rec_number: yup
    .number()
    .required('Ο αριθμός εγγραφής απαιτείται')
    .positive('Ο αριθμός εγγραφής πρέπει να είναι θετικός αριθμός')
    .integer('Ο αριθμός εγγραφής πρέπει να είναι ακέραιος'),

  rec_date: yup
    .date()
    .nullable(true),

  episodes: yup
    .array()
    .of(yup.number().positive().integer())
    .nullable(true),

  rec_notes: yup
    .string()
    .nullable(true),
});

const headerNames = {
  p_id: 'Παραγωγή',
  rec_number: 'Αριθμός Εγγραφής',
  rec_date: 'Ημερομηνία Εγγραφής',
  episodes: 'Επεισόδια',
  rec_notes: 'Σημειώσεις'
};

const URL = `recordings`;

const RecordingsEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: productions } = useSWR(`/productions`);
  const { notifier } = useNotifier();

  const [state, setState] = useState(null);
  const [errorMessage, setErrorMessage] = useState();
  const [errors, setErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  const clientKey = `/${URL}/${id}`;

  if (!state) {
    mutate(clientKey);
  }

  const { data: recording } = useSWR(clientKey, {
    revalidateOnFocus: false,
    revalidateOnMount: true,
    onSuccess: (data) => {
      if (data) {
        setState(data);
        setDrawerOpen(true);
        setIsUpdating(false);
      }
    },
    onError: (err) => {
      setErrorMessage(err);
    }
  });

  function updateField(fieldName, value) {
    setState(prevState => ({ ...prevState, [fieldName]: value }));
  }

  // Fetch existing recordings for validation
  const { data: existingRecordings } = useSWR(
    state?.p_id ? `/recordings/of-production/${state.p_id}` : null
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Normalize p_id to ensure it's a number before validation
      const normalizedState = {
        ...state,
        p_id: state.p_id ? Number(state.p_id) : null,
        rec_number: state.rec_number ? Number(state.rec_number) : null,
      };

      // Custom validation: Check if rec_number already exists for this production (excluding current record)
      if (normalizedState?.rec_number && existingRecordings) {
        const duplicate = existingRecordings.find(
          (r) => r.rec_number === normalizedState.rec_number && r.id !== normalizedState.id
        );
        if (duplicate) {
          setErrors({
            rec_number: `Ο αριθμός εγγραφής ${normalizedState.rec_number} υπάρχει ήδη για αυτή την παραγωγή`,
          });
          return;
        }
      }

      await validationSchema.validate(normalizedState, { abortEarly: false });
      setErrors({});
      setIsUpdating(true);

      // Prepare data for API - only include fields that can be updated
      const submitData = {
        p_id: normalizedState.p_id,
        rec_number: normalizedState.rec_number,
        rec_date: normalizedState.rec_date || null,
        episodes: normalizedState.episodes || [],
        rec_notes: normalizedState.rec_notes || null,
      };

      api.patch(`/${URL}/${normalizedState.id}`, submitData)
        .then(response => {
          mutate(`/${URL}`);
          mutate(`/${URL}/of-production/${state.p_id}`);
          mutate(clientKey);
          notifier("Εγγραφή ενημερώθηκε.", { variant: "success" });
          setDrawerOpen(false);
          navigate(-1);
        })
        .catch(error => {
          setErrorMessage(error);
          notifier(<ErrorNotification error={error} />, { variant: "error" });
        })
        .finally(() => {
          setIsUpdating(false);
        });

    } catch (validationErrors) {
      const formattedErrors = {};
      validationErrors.inner.forEach((error) => {
        formattedErrors[error.path] = error.message;
      });
      setErrors(formattedErrors);
    }
  };

  const handleSave = () => {
    const form = document.getElementById(`${URL}-form`);
    if (form) {
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const [drawerOpen, setDrawerOpen] = useState(true);

  return (
    <>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCancel}
        PaperProps={{
          style: {
            width: '100%',
            height: '100vh',
            maxWidth: '800px',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Card
          sx={{
            width: '100%',
            maxWidth: 800,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header Section */}
          <Box>
            <CardHeader
              title={<Typography variant="h3" gutterBottom>Επεξεργασία Εγγραφής</Typography>}
            />
          </Box>

          {/* Scrollable Content Section */}
          <CardContent
            sx={{
              display: 'flex',
              height: '100vh',
              flexGrow: 1,
              overflowY: 'auto',
              margin: 'auto',
            }}
          >
            <form id={`${URL}-form`} onSubmit={handleSubmit}>
              {state && (
                <RecordingsForm
                  state={state}
                  errors={errors}
                  updateField={updateField}
                  headerNames={headerNames}
                  productions={productions}
                  existingRecordings={existingRecordings}
                />
              )}
            </form>
            {isUpdating && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  zIndex: 1000,
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </CardContent>

          {/* Toolbar (Footer Section) */}
          <Box>
            <FormAddEditToolbar
              handleSave={handleSave}
              handleCancel={handleCancel}
            />
          </Box>
        </Card>
      </Drawer>
    </>
  );
};

export default RecordingsEdit;

